import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface ComplianceItem {
  id: string;
  employeeId: string | null;
  countryCode: string;
  kind: string;
  dueDate: string | null;
  status: 'open' | 'in_progress' | 'done' | 'overdue' | 'waived';
  metadata: Record<string, unknown> | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface ComplianceSummary {
  open: number;
  overdue: number;
  in_progress: number;
  done_30d: number;
}

export function useComplianceItems(params: { status?: string; country?: string } = {}) {
  return useQuery({
    queryKey: ['compliance', 'items', params],
    queryFn: async () => {
      const r = await api.get<{ items: ComplianceItem[] }>('/api/compliance', { params });
      return r.data.items;
    },
  });
}

export function useComplianceSummary() {
  return useQuery({
    queryKey: ['compliance', 'summary'],
    queryFn: async () => {
      const r = await api.get<ComplianceSummary>('/api/compliance/summary');
      return r.data;
    },
  });
}

export function useComplianceScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<{ created: number; existing: number; items: ComplianceItem[] }>('/api/compliance/scan');
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance'] }),
  });
}

export function useResolveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post<ComplianceItem>(`/api/compliance/${id}/resolve`);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance'] }),
  });
}

export function useWaiveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post<ComplianceItem>(`/api/compliance/${id}/waive`);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance'] }),
  });
}

const KIND_LABEL: Record<string, { en: string; ja: string }> = {
  jp_shakaihoken_kakutoku:      { en: 'JP social insurance enrollment',          ja: '健康保険・厚生年金 資格取得届' },
  jp_koyou_kakutoku:            { en: 'JP employment insurance enrollment',      ja: '雇用保険 資格取得届' },
  jp_my_number_collect:         { en: 'Collect マイナンバー',                    ja: 'マイナンバーの収集 (本人確認+番号確認)' },
  jp_my_number_annual_check:    { en: 'Annual マイナンバー confirmation',        ja: 'マイナンバー 年次確認' },
  jp_sanetei_kiso:              { en: 'JP 算定基礎届 annual filing',             ja: '算定基礎届 (年次)' },
  jp_nencho_collection:         { en: 'JP 年末調整 forms collection',            ja: '年末調整 申告書回収' },
  jp_kyuyo_shiharai_houkoku:    { en: 'JP 給与支払報告書 to municipalities',      ja: '給与支払報告書 (市町村)' },
  jp_hotei_chosho:              { en: 'JP 法定調書合計表',                       ja: '法定調書合計表 (税務署)' },
  us_i9_section2:               { en: 'US I-9 Section 2',                        ja: 'US I-9 Section 2 (3日以内)' },
  us_w4_on_file:                { en: 'US W-4 on file',                          ja: 'US W-4 ファイル登録' },
  uk_right_to_work_check:       { en: 'UK Right to Work check',                  ja: 'UK 就労資格確認' },
  uk_paye_registration:         { en: 'UK PAYE registration',                    ja: 'UK PAYE 登録' },
  de_sozialversicherung_anmeldung: { en: 'DE Sozialversicherung Anmeldung',      ja: 'DE 社会保険登録' },
};

export function complianceLabel(kind: string, locale: string): string {
  const meta = KIND_LABEL[kind];
  if (!meta) return kind;
  return locale.startsWith('ja') ? meta.ja : meta.en;
}
